import { Children, useCallback, useMemo } from 'react'
import { Box, chakra, Flex, Icon, Text } from '@chakra-ui/react'
import {
    chakraComponents,
    ChakraStylesConfig,
    components,
    DropdownIndicatorProps,
    InputProps,
    MenuListProps,
    OptionProps,
    Select,
} from 'chakra-react-select'
import { ChevronDown } from 'emotion-icons/feather'
import { append, unique } from 'lib/array'
import { isServerSide } from 'lib/env'
import useIsMobile from 'hooks/useIsMobile'
import { getLocationInputOptions } from 'containers/jobs-board/components/LocationInput/locationOptions'
import {
    CountryCodeToCountryName,
    Facet,
    isLocationCityOption,
    isLocationRegionOption,
    isLocationCountryCodeOption,
} from 'containers/jobs-board/components/options'
import { useJobsSearchContext } from 'containers/jobs-board/context/JobsSearchContext'
import { Params, queryParamName } from 'containers/jobs-board/hooks/useParams'

const MAX_OPTIONS_DISPLAYED = 10

const DropdownIndicator = (props: DropdownIndicatorProps) => {
    return (
        <components.DropdownIndicator {...props}>
            <Icon
                transition="all .1s"
                transform={props?.isFocused ? 'rotate(180deg)' : 'rotate(0deg)'}
                as={ChevronDown}
                strokeWidth="2px"
                fontSize="22px"
                color={props.isFocused ? 'secondary.500' : 'text.500'}
                className="dropdown-icon"
            />
        </components.DropdownIndicator>
    )
}

const onLocationSelection = (
    location: any,
    filters: Params,
    updateFilters: (params: Params) => void,
) => {
    const { type, value } = location

    if (type === queryParamName.locationCity) {
        updateFilters({
            locationCity: unique(append(filters.locationCity, value)),
        })
    } else if (type === queryParamName.locationCountryCode) {
        updateFilters({
            locationCountryCode: unique(append(filters.locationCountryCode, value)),
        })
    } else if (type === queryParamName.locationRegion) {
        updateFilters({
            locationRegion: unique(append(filters.locationRegion, value)),
        })
    }
}

const Option = (props: OptionProps<any>) => {
    const { filters, updateFilters } = useJobsSearchContext()

    const isFocused = props.isFocused // options are focusable by down/up arrows
    const focusedStyles = {
        backgroundColor: 'gray.bg_dark',
    }
    const defaultStyles = isFocused
        ? focusedStyles
        : {
              backgroundColor: 'gray.background',
              borderColor: 'gray.background',
          }

    const onClick = useCallback(() => {
        onLocationSelection(props.data, filters, updateFilters)
    }, [props.data, filters, updateFilters])

    return (
        <chakra.button
            width="fit-content"
            mt="8px"
            mr="8px"
            fontSize="13px"
            fontWeight="medium"
            lineHeight="18px"
            sx={{
                div: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderWidth: '1px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    ...defaultStyles,
                },
                'div:focus, div:hover': focusedStyles,
            }}
            onClick={onClick}
        >
            <components.Option {...props} />
        </chakra.button>
    )
}

const Input = (props: InputProps) => {
    return <chakraComponents.Input placeholder="Location..." {...props} />
}

const sxControl = {
    ':hover': {
        '.dropdown-icon': {
            color: 'secondary.500',
        },
    },
}

const Control = (props: any) => {
    return (
        <Box sx={sxControl}>
            <chakraComponents.Control {...props} />
        </Box>
    )
}

const MenuList = (props: MenuListProps) => {
    const children = Children.toArray(props.children)

    const options = useMemo(() => {
        if (Array.isArray(children)) {
            return children.slice(0, MAX_OPTIONS_DISPLAYED)
        }
        return children
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.children])

    return (
        <chakraComponents.MenuList {...props}>
            <Flex direction="column" gridGap="12px">
                <Text variant="bodyTextSmall" color="text.secondary">
                    Suggested Locations:
                </Text>
                <Box>{options}</Box>
            </Flex>
        </chakraComponents.MenuList>
    )
}

const chakraStyles: ChakraStylesConfig = {
    container: provided => ({
        ...provided,
        width: { base: '141px', md: '169px' },
    }),
    control: (provided, state) => ({
        ...provided,
        minHeight: { base: '40px', md: '44px' },
        bgColor: 'white',
        borderColor: 'gray.dividers',
        cursor: state.isFocused ? 'text' : 'pointer',
        transitionDuration: '0ms',
        '#react-select-location-input-placeholder': {
            pointerEvents: 'none',
        },
        _focus: {
            color: 'text.500',
            borderColor: 'secondary.500',
        },
        _hover: {
            color: 'secondary.500',
            borderColor: 'secondary.500',
            '#react-select-location-input-placeholder': {
                color: state.isFocused ? 'transparent' : 'secondary.500',
            },
        },
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    clearIndicator: () => ({
        display: 'none',
    }),
    dropdownIndicator: provided => ({
        ...provided,
        bgColor: 'white',
    }),
    placeholder: (provided, state) => ({
        ...provided,
        fontWeight: 'medium',
        color: state.isFocused ? 'transparent' : 'text.500',
    }),

    menuList: provided => ({
        ...provided,
        maxH: 'fit-content',
        minW: { base: 'calc(100vw - 24px)', md: '330px' },
        pt: { base: '24px' },
        pb: { base: '26px' },
        pl: { base: '24px' },
        pr: { base: '24px' },
        borderRadius: '12px',
    }),
    menu: provided => {
        return {
            ...provided,
            mx: { base: '12px', md: '0' },
            width: 'auto',
        }
    },
    groupHeading: provided => ({
        ...provided,
        display: 'flex',
        fontSize: '16px',
        color: 'text.secondary',
        width: '100%',
        p: '0px',
        mb: '12px !important',
    }),
    input: provided => ({
        ...provided,
        w: '80px',
        _placeholder: {
            color: 'transparent',
            fontWeight: 'medium',
        },
        _focus: {
            _placeholder: {
                color: 'text.secondary',
            },
        },
    }),
}

const id = 'location-input'

export const LocationInput = ({
    countryCodeToCountryName,
    facets,
}: {
    countryCodeToCountryName: CountryCodeToCountryName
    facets?: Facet[]
}) => {
    const { filters, updateFilters } = useJobsSearchContext()
    const isMobile = useIsMobile(false)

    const options = getLocationInputOptions(countryCodeToCountryName, facets)

    const isOptionSelected = (option: any) => {
        if (isLocationCityOption(option)) {
            return filters.locationCity?.includes(option.value)!!
        }
        if (isLocationCountryCodeOption(option)) {
            return filters.locationCountryCode?.includes(option.value)!!
        }
        if (isLocationRegionOption(option)) {
            return filters.locationRegion?.includes(option.value)!!
        }
        return false
    }

    const onChange = useCallback(
        (selectedOptions: any) => {
            const selectedLocation = selectedOptions[0]
            if (selectedLocation) {
                onLocationSelection(selectedLocation, filters, updateFilters)
            }
        },
        [filters, updateFilters],
    )

    return (
        <Select
            chakraStyles={chakraStyles}
            placeholder="Location"
            components={{ DropdownIndicator, Option, MenuList, Input, Control }}
            menuPortalTarget={isServerSide ? undefined : document.body}
            // NB: See 'https://github.com/csandman/chakra-react-select#caveats'.
            styles={{
                menuPortal: provided => {
                    let positionProps
                    if (!isServerSide && isMobile) {
                        positionProps = { left: 0 }
                    }
                    return {
                        ...provided,
                        ...positionProps,
                        zIndex: 'var(--chakra-zIndices-popover)',
                    }
                },
            }}
            isMulti
            isSearchable
            inputId={id}
            instanceId={id}
            options={options}
            isOptionSelected={isOptionSelected}
            // NB: We do not keep selected locations in this field's state,
            //     but instead store them in shared context (see `Params`).
            value={[]}
            onChange={onChange}
            tabSelectsValue={false}
        />
    )
}
